import type { ProjectTaskListItem } from "@/modules/management/project/domain/types";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import { managementProjectApi } from "../api/client";

const TaskFormSchema = z.object({
  title: z.string().min(1, "ກະລຸນາປ້ອນຊື່ວຽກ"),
  assigneeId: z.string().optional().nullable(),
  dueDate: z.string().optional().nullable(),
  priority: z.enum(["high", "medium", "low"]).optional().nullable(),
  progressPercent: z.number().int().min(0).max(100).default(0),
  status: z.enum(["pending", "in_progress", "done"]).default("pending"),
});

type TaskFormValues = z.infer<typeof TaskFormSchema>;

type TaskFormDialogProps = {
  projectId: string;
  projectLevel?: "position" | "department" | "organization";
  projectPositionId?: string;
  projectDepartmentId?: string;
  task?: ProjectTaskListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function TaskFormDialog({
  projectId,
  projectLevel = "organization",
  projectPositionId,
  projectDepartmentId,
  task,
  open,
  onOpenChange,
  onSuccess,
}: TaskFormDialogProps) {
  const userLookupExtraParams: Record<string, string> =
    projectLevel === "position" && projectPositionId?.trim()
      ? { positionIds: projectPositionId.trim() }
      : projectLevel === "department" && projectDepartmentId?.trim()
        ? { departmentId: projectDepartmentId.trim() }
        : {};
  const isEdit = Boolean(task?.id);

  const methods = RHF.useForm<TaskFormValues>({
    defaultValues: {
      title: "",
      assigneeId: undefined,
      dueDate: undefined,
      priority: undefined,
      progressPercent: 0,
      status: "pending",
    },
    resolver: zodResolver(TaskFormSchema),
  });

  useEffect(() => {
    if (open && task) {
      methods.reset({
        title: task.title,
        assigneeId: task.assigneeId ?? undefined,
        dueDate: task.dueDate ?? undefined,
        priority: task.priority ?? undefined,
        progressPercent: task.progressPercent ?? 0,
        status: task.status,
      });
    } else if (open && !task) {
      methods.reset({
        title: "",
        assigneeId: undefined,
        dueDate: undefined,
        priority: undefined,
        progressPercent: 0,
        status: "pending",
      });
    }
  }, [open, task, methods]);

  const onSubmit = async (values: TaskFormValues) => {
    try {
      if (isEdit && task) {
        await managementProjectApi.updateTask(projectId, task.id, values);
        toast.success("ບັນທຶກວຽກຍ່ອຍສຳເລັດ");
      } else {
        await managementProjectApi.createTask(projectId, values);
        toast.success("ສ້າງວຽກຍ່ອຍສຳເລັດ");
      }
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "ແກ້ໄຂວຽກຍ່ອຍ" : "ເພີ່ມວຽກຍ່ອຍ"}</DialogTitle>
        </DialogHeader>
        <FormRoot<TaskFormValues>
          methods={methods}
          onSubmit={onSubmit}
          className="space-y-4"
        >
          <FormInput
            name="title"
            label="ຊື່ວຽກ"
            placeholder="ປ້ອນຊື່ວຽກ"
            requiredMark
          />
          <FormInfiniteCombobox<LookupItem>
            name="assigneeId"
            label="ຜູ້ຮັບຜິດຊອບ"
            queryKey={[
              "users",
              "task-form",
              projectLevel,
              projectPositionId ?? "",
              projectDepartmentId ?? "",
            ]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/users/lookup`,
                args,
                Object.keys(userLookupExtraParams).length > 0
                  ? userLookupExtraParams
                  : undefined,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ..."
            clearable
          />
          <FormInput
            name="dueDate"
            label="ວັນສົ່ງ"
            placeholder="ປ້ອນວັນສົ່ງ"
            type="date"
            requiredMark
          />

          <FormSelect
            name="priority"
            label="ຄວາມສຳຄັນ"
            options={[
              { value: "__none__", label: "— ບໍ່ກຳນົດ —" },
              { value: "high", label: "ສູງ" },
              { value: "medium", label: "ກາງ" },
              { value: "low", label: "ຕ່ຳ" },
            ]}
          />
          <FormNumber
            name="progressPercent"
            label="% ຄວາມຄືບໜ້າ (0–100)"
            min={0}
            max={100}
          />
          <FormSelect
            name="status"
            label="ສະຖານະ"
            options={[
              { value: "pending", label: "ລໍຖ້າ" },
              { value: "in_progress", label: "ກຳລັງດຳເນີນງານ" },
              { value: "done", label: "ສຳເລັດ" },
            ]}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit">{isEdit ? "ບັນທຶກ" : "ສ້າງວຽກຍ່ອຍ"}</Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
